import { useState, useEffect, Fragment, useContext } from 'react';
import { ref, onValue } from "firebase/database";
import { db } from '../../utils/firebaseApp.js';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { AuthContext } from "../../utils/auth.js";

export default function WebhookLogs({ userId }) {
    const { currentUser } = useContext(AuthContext);
    const adminEmails = ['barzoiu.alexandru@gmail.com', "alexraducu03@gmail.com"];
    const isAdmin = adminEmails.includes(currentUser.email);

    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [adminUserId, setAdminUserId] = useState('');
    const [searchedUserId, setSearchedUserId] = useState('');
    const [selectedType, setSelectedType] = useState('all');

    useEffect(() => {
        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : userId;

        if (!targetUserId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const logsRef = ref(db, `webhookLogsV2/${targetUserId}`);

        // Set up real-time listener
        const unsubscribe = onValue(logsRef, (snapshot) => {
            try {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const logsArray = [];

                    Object.keys(data).forEach((logId) => {
                        const log = data[logId];

                        // Extract status from response
                        let status = 'N/A';
                        if (log.response && log.response.status !== undefined) {
                            status = log.response.status;
                        }

                        const eventType = log.event || log.body?.event || 'N/A';
                        const [feature, type] = eventType.includes('.')
                            ? eventType.split('.')
                            : [eventType, 'N/A'];

                        // Check if response was received
                        const hasResponse = log.response &&
                            typeof log.response === 'object' &&
                            Object.keys(log.response).length > 0 &&
                            !(typeof log.response === 'string' && log.response.trim() === '');

                        logsArray.push({
                            id: logId,
                            eventType: eventType,
                            feature: feature,
                            type: type,
                            body: log.body || {},
                            options: log.options || {},
                            response: log.response || {},
                            error: log.error || null,
                            status: status,
                            received: hasResponse,
                            url: log.url || null,
                            timestamp: log.timestamp || Date.now(),
                        });
                    });

                    // Sort by timestamp, most recent first
                    logsArray.sort((a, b) => {
                        const timeA = new Date(a.timestamp).getTime();
                        const timeB = new Date(b.timestamp).getTime();
                        return timeB - timeA;
                    });

                    setLogs(logsArray);
                } else {
                    setLogs([]);
                }
            } catch (error) {
                console.error('Error processing webhook logs:', error);
                setLogs([]);
            } finally {
                setIsLoading(false);
            }
        }, (error) => {
            console.error('Error listening to webhook logs:', error);
            setLogs([]);
            setIsLoading(false);
        });

        // Cleanup listener on unmount or when dependencies change
        return () => unsubscribe();
    }, [userId, searchedUserId, isAdmin]);

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return date.toLocaleDateString(undefined, options);
    };

    const formatJSON = (json) => {
        try {
            return JSON.stringify(json, null, 2);
        } catch (e) {
            return 'Invalid JSON';
        }
    };

    const getStatusBadgeColor = (status) => {
        if (typeof status === 'string') {
            status = parseInt(status, 10);
        }

        if (status >= 200 && status < 300) {
            return 'bg-green-100 text-green-800';
        } else if (status >= 400 && status < 500) {
            return 'bg-yellow-100 text-yellow-800';
        } else if (status >= 500) {
            return 'bg-red-100 text-red-800';
        }
        return 'bg-gray-100 text-gray-800';
    };

    const toggleRow = (logId) => {
        setExpandedRow(expandedRow === logId ? null : logId);
    };

    // Extract unique types from logs with specific order
    const allTypes = [...new Set(logs.map(log => log.type).filter(type => type !== 'N/A'))];

    // Define the desired order
    const typeOrder = ['processed', 'redo', 'confirmed', 'edited', 'failed'];

    // Sort types: first by defined order, then alphabetically for others
    const sortedTypes = allTypes.sort((a, b) => {
        const indexA = typeOrder.indexOf(a);
        const indexB = typeOrder.indexOf(b);

        // If both are in the order list, sort by their position
        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }

        // If only a is in the order list, it comes first
        if (indexA !== -1) return -1;

        // If only b is in the order list, it comes first
        if (indexB !== -1) return 1;

        // Both are not in the order list, sort alphabetically
        return a.localeCompare(b);
    });

    const uniqueTypes = ['all', ...sortedTypes];

    // Type color mapping
    const getTypeColor = (type) => {
        // Specific colors for known types
        const typeColorMap = {
            'all': 'bg-gray-100 text-gray-800 hover:bg-gray-200',
            'processed': 'bg-green-100 text-green-800 hover:bg-green-200',
            'redo': 'bg-orange-100 text-orange-800 hover:bg-orange-200',
            'confirmed': 'bg-blue-100 text-blue-800 hover:bg-blue-200',
            'edited': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
            'failed': 'bg-red-100 text-red-800 hover:bg-red-200',
            'pending': 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
            'editing': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
        };

        // If type has a specific color, use it
        if (typeColorMap[type]) {
            return typeColorMap[type];
        }

        // Otherwise, assign color based on hash of type name for consistency
        const fallbackColors = [
            'bg-pink-100 text-pink-800 hover:bg-pink-200',
            'bg-orange-100 text-orange-800 hover:bg-orange-200',
            'bg-teal-100 text-teal-800 hover:bg-teal-200',
            'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
            'bg-amber-100 text-amber-800 hover:bg-amber-200',
            'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
        ];

        // Simple hash function for consistent color assignment
        let hash = 0;
        for (let i = 0; i < type.length; i++) {
            hash = type.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % fallbackColors.length;
        return fallbackColors[index];
    };

    // Filter logs by selected type
    const filteredLogs = selectedType === 'all'
        ? logs
        : logs.filter(log => log.type === selectedType);

    // Pagination logic
    const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentLogs = filteredLogs.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setExpandedRow(null); // Close any expanded rows when changing page
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
        setExpandedRow(null);
    };

    const handleAdminSearch = () => {
        if (adminUserId.trim()) {
            setSearchedUserId(adminUserId.trim());
            setCurrentPage(1);
            setExpandedRow(null);
        }
    };

    const handleAdminSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAdminSearch();
        }
    };

    const handleClearSearch = () => {
        setAdminUserId('');
        setSearchedUserId('');
        setCurrentPage(1);
        setExpandedRow(null);
    };

    const handleTypeFilter = (type) => {
        setSelectedType(type);
        setCurrentPage(1);
        setExpandedRow(null);
    };

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center h-32">
                <div role="status">
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-indigo-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <>
                {isAdmin && (
                    <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                                Admin
                            </span>
                            <h3 className="text-sm font-semibold text-indigo-900">View logs for any user</h3>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <input
                                type="text"
                                value={adminUserId}
                                onChange={(e) => setAdminUserId(e.target.value)}
                                onKeyPress={handleAdminSearchKeyPress}
                                placeholder="Enter User ID"
                                className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                                onClick={handleAdminSearch}
                                disabled={!adminUserId.trim()}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Search className="h-4 w-4" />
                                Search
                            </button>
                            {searchedUserId && (
                                <button
                                    onClick={handleClearSearch}
                                    className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        {searchedUserId && (
                            <p className="mt-2 text-sm text-indigo-700">
                                Viewing logs for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                            </p>
                        )}
                    </div>
                )}
                <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900">No Webhook Logs yet</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            Your webhook logs will appear here once webhook events are triggered.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold leading-6 text-gray-900">Webhook Logs</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        View all webhook events that have been sent to your endpoint. Click on a row to see detailed JSON data.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 flex items-center space-x-2">
                    <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
                        Show:
                    </label>
                    <div className="relative">
                        <select
                            id="itemsPerPage"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="text-sm border border-gray-300 rounded-md pl-2 pr-8 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
                        >
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <span className="text-sm text-gray-700">per page</span>
                </div>
            </div>

            {/* Admin Search */}
            {isAdmin && (
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                            Admin
                        </span>
                        <h3 className="text-sm font-semibold text-indigo-900">View logs for any user</h3>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={adminUserId}
                            onChange={(e) => setAdminUserId(e.target.value)}
                            onKeyPress={handleAdminSearchKeyPress}
                            placeholder="Enter User ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleAdminSearch}
                            disabled={!adminUserId.trim()}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                        {searchedUserId && (
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {searchedUserId && (
                        <p className="mt-2 text-sm text-indigo-700">
                            Viewing logs for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                        </p>
                    )}
                </div>
            )}

            {/* Type Filter Badges */}
            {logs.length > 0 && uniqueTypes.length > 1 && (
                <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-700">Filter by type:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {uniqueTypes.map((type) => {
                            const count = type === 'all'
                                ? logs.length
                                : logs.filter(log => log.type === type).length;
                            const isSelected = selectedType === type;

                            return (
                                <button
                                    key={type}
                                    onClick={() => handleTypeFilter(type)}
                                    className={`inline-flex items-center gap-x-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${getTypeColor(type)
                                        } ${isSelected
                                            ? 'ring-2 ring-offset-1 ring-indigo-600'
                                            : ''
                                        }`}
                                >
                                    <span className="capitalize">{type}</span>
                                    <span className={`inline-flex items-center justify-center w-5 h-5 text-xs rounded-full ${isSelected ? 'bg-white bg-opacity-60' : 'bg-black bg-opacity-10'
                                        }`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-4 flow-root">
                {filteredLogs.length === 0 ? (
                    <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900">No logs found for "{selectedType}"</h2>
                            <p className="mt-2 text-sm text-gray-500">
                                Try selecting a different type or view all logs.
                            </p>
                            <button
                                onClick={() => handleTypeFilter('all')}
                                className="mt-4 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                            >
                                View All Logs
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead>
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                            WEBHOOK ID
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            FEATURE
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            TYPE
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            DELIVERED
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            RECEIVED
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            DATE
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            ACTIONS
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {currentLogs.map((log) => (
                                        <Fragment key={log.id}>
                                            <tr className={`hover:bg-gray-50 ${log.error ? 'bg-red-50' : ''}`}>
                                                <td className="py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 break-all">
                                                    {log.id}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    {log.feature}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    {log.type}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    {log.error ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-red-700 font-medium">Failed</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5">
                                                            <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-green-700 font-medium">Sent</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    {log.received ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex items-center gap-1.5">
                                                                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                                <span className="text-green-700 font-medium">Yes</span>
                                                            </div>
                                                            {log.status !== 'N/A' && (
                                                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${getStatusBadgeColor(log.status)}`}>
                                                                    {log.status}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5">
                                                            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="text-gray-600 font-medium">No</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    {formatDate(log.timestamp)}
                                                </td>
                                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleRow(log.id)}
                                                        className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                    >
                                                        {expandedRow === log.id ? 'Hide' : 'View'} Details
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedRow === log.id && (
                                                <tr>
                                                    <td colSpan="6" className="px-4 py-4 bg-gray-50">
                                                        <div className="grid grid-cols-1 gap-4">
                                                            {log.error && (
                                                                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                                                                    <div className="flex items-start">
                                                                        <div className="flex-shrink-0">
                                                                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                                            </svg>
                                                                        </div>
                                                                        <div className="ml-3 flex-1">
                                                                            <h3 className="text-sm font-semibold text-red-900 mb-2">Error Occurred</h3>
                                                                            <pre className="bg-white p-3 rounded border border-red-200 text-xs overflow-auto max-h-48 text-red-900">
                                                                                {formatJSON(log.error)}
                                                                            </pre>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {log.url && log.url.trim() !== '' && (
                                                                <div>
                                                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Webhook URL</h3>
                                                                    <div className="bg-white p-3 rounded border border-gray-200 text-xs break-all">
                                                                        <a href={log.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                                                                            {log.url}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <h3 className="text-sm font-semibold text-gray-900 mb-2">Body</h3>
                                                                <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-auto max-h-64">
                                                                    {formatJSON(log.body)}
                                                                </pre>
                                                            </div>
                                                            <div>
                                                                <h3 className="text-sm font-semibold text-gray-900 mb-2">Response</h3>
                                                                <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-auto max-h-64">
                                                                    {formatJSON(log.response)}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {filteredLogs.length > 0 && totalPages > 1 && (
                <div className="mt-4 px-4 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                        <div className="flex items-center text-sm text-gray-500">
                            Showing {startIndex + 1} to {Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length} results
                            {selectedType !== 'all' && (
                                <span className="ml-1">
                                    (filtered from {logs.length} total)
                                </span>
                            )}
                        </div>

                        <div className="flex items-center space-x-1">
                            {/* First Page */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="First page"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <ChevronLeft className="h-4 w-4 -ml-1" />
                            </button>

                            {/* Previous Page */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                            </button>

                            {/* Page Numbers */}
                            <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let pageNumber;
                                    if (totalPages <= 5) {
                                        pageNumber = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNumber = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNumber = totalPages - 4 + i;
                                    } else {
                                        pageNumber = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNumber}
                                            onClick={() => handlePageChange(pageNumber)}
                                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNumber
                                                ? 'bg-indigo-600 text-white'
                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Next Page */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </button>

                            {/* Last Page */}
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Last page"
                            >
                                <ChevronRight className="h-4 w-4" />
                                <ChevronRight className="h-4 w-4 -ml-1" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

